// src/belediye/components/notifications/SettingsModal.js
//
// Bildirim ayarları MODALI (ayrı sayfa yerine açılır pencere). Ses/toast + kaynak
// bazlı sustur + sessiz saatler. Anlık kaydeder (Redux slice).
// Sistem tasarım dili (BasicModal + btn-md).
//
// FAZ-79 — ÜÇ EKSİK KAPANDI (kullanıcı, 2026-07-30: "ilgili ayarlar kısmında
// kapatma onayı, talepler, duyuru, sistem, mesajlar gibi şeyler ekle, bunların
// ayarlaması falan yap"):
//
//   1) MESAJLAR LİSTEDE YOKTU. `SOURCE_CATEGORIES` dört kaynak sayıyordu
//      (close_approval · gorev · duyuru · sistem) — `mesajlasma` HİÇ yoktu.
//      Mesajlaşma bildirim üreten en gürültülü kaynak olduğu hâlde tek
//      kapatılamayan kaynaktı. Eklendi.
//
//   2) SESSİZ SAATLER MODELDE VAR, EKRANDA YOKTU. `NotificationPreference`
//      `quiet_hours_start/end` alanlarını FAZ-31'den beri taşıyor ve uç PUT ile
//      kabul ediyor; hiçbir yüzey yazmıyordu → ölü alan. Eklendi.
//
//   3) KAPALI MODÜLÜN KAYNAĞI LİSTEDE DURUYORDU. Mesajlaşma/talepler modülü
//      kapalıyken o satır hiçbir işe yaramıyordu (o kaynaktan bildirim
//      üretilmiyor). Modül kapısına bağlandı.
//
// Kaynak listesinin kendisi notificationMeta'nın SEGMENTS'inden TÜRETİLİR —
// yeni bir kaynak eklendiğinde iki yerde ayrı ayrı güncelleme gerekmesin
// (bildirim merkezinde sekme var ama ayarlarda satır yok gibi sapmalar).

import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import BasicModal from "../ui/BasicModal";
import {
  fetchPreferences,
  savePreferences,
  selectPreferences,
  selectPreferencesLoaded,
  selectPreferencesSaving,
} from "../../redux/slices/belediyeNotificationsSlice";
import useModuleEnabled from "../../hooks/useModuleEnabled";
import { SOURCE_LABELS } from "./notificationMeta";
// FAZ-84 — telefon bildirimi: bu cihazın kurulum rehberi.
// FAZ-102: yönetici "kim kurmuş" raporu buradan TAMAMEN kaldırıldı (kullanıcı,
// 2026-08-02: "bu kısmı tamamen kaldıralım, ne lokalde ne canlıda olsun,
// gereksiz bir şey"). Ayarlar penceresi KİŞİSEL bir yüzeydir; yönetim raporu
// oraya ait değildi. Rapora bağlı uç, model ve cihaz-hâli kaydı da silindi —
// çağıranı kalmayan veri toplamak, toplanmaması gereken veridir.
import PushKurulumPaneli from "./PushKurulumPaneli";
import "./SettingsModal.css";

// ── "NELER BİLDİRİLSİN" — TEK LİSTE, GRUPLANMIŞ (FAZ-106/2) ────────────────
//
// Kullanıcı (2026-08-05): "Burada neden iki defa 'kapatma onayı' ve 'yeni
// başvuru' var? Bu tür şeylere gerek yok, daha kullanıcı dostu tasarla."
//
// HAKLIYDI. İlk turda ekranda İKİ ayrı blok vardı:
//   • "Neler bildirilsin"     → KAYNAK anahtarı (muted_categories)
//   • "Ayrıntılı bildirimler" → TÜR anahtarı    (tur_tercihleri)
// Bunlar farklı MEKANİZMALAR (biri susturur, diğeri satırı hiç üretmez) ama
// kullanıcı için aynı cümleyi kuruyorlar: "bu bildirim bana gelsin mi". Aynı
// soruyu iki kez sormak, hangisinin kazandığını da bilinmez kılıyordu.
//
// Yeni kural: EKRANDA HER ŞEY BİR KEZ GEÇER.
//   • `basvuru` ve `close_approval` kaynaklarının her olayı artık bir TÜRE
//     bağlı (backend: topics.TUR_KAPSAMLI_KAYNAKLAR) → o iki kaynak satırı
//     TÜMDEN kalktı, yerlerini türleri aldı.
//   • Kalan kaynaklar (talep/duyuru/sistem/mesaj) tek satır olarak durur.
//   • Satırlar bildirim merkezindeki SEKMELERLE aynı gruplara ayrılır: kişi
//     zilde gördüğü bölmeyi burada da aynı adla bulur.
const SOURCE_ROWS = [
  { key: "gorev", module: "internal_tasks" },
  { key: "mesajlasma", module: "mesajlasma" },
  { key: "duyuru", module: null },
  { key: "sistem", module: null },
];

// Satır etiketi = YAPILAN İŞ, modülün adı değil. "Talepler" bir menü başlığıdır;
// burada sorulan soru "hangi bildirim" olduğu için "Talep bildirimleri" okunur.
// Satır etiketi grup başlığını TEKRAR ETMEZ: "Duyurular" grubunun içinde
// "Duyurular" satırı, aynı kelimeyi iki farklı anlamda (bölme adı / anahtar
// adı) kullanmak olurdu.
const SOURCE_LABEL_OVERRIDE = {
  gorev: "Talep bildirimleri",
  mesajlasma: "Yeni mesaj",
  duyuru: "Kurum duyuruları",
  sistem: "Sistem bilgilendirmeleri",
};

const HINTS = {
  gorev: "Atama, tamamlama, iptal ve süre uyarıları.",
  duyuru: "Kurum içi duyurular.",
  sistem: "Hesabınızla ilgili bilgilendirmeler.",
  mesajlasma: "Sohbetlerde yeni mesaj.",
};

// Grup sırası bildirim merkezindeki sekme sırasıyla aynıdır (notificationMeta
// SEGMENTS). Anahtar = segment; `kaynaklar` o gruba düşen source_module'ler.
const GRUPLAR = [
  { key: "basvuru", label: "Başvurular", kaynaklar: ["basvuru", "close_approval"] },
  { key: "gorev", label: "Talepler", kaynaklar: ["gorev"] },
  { key: "mesajlasma", label: "Mesajlar", kaynaklar: ["mesajlasma"] },
  { key: "duyuru", label: "Duyurular", kaynaklar: ["duyuru", "sistem", "sms"] },
];

function Toggle({ checked, onChange, disabled, label, hint }) {
  return (
    <label className={`sm-row ${disabled ? "is-disabled" : ""}`}>
      <span className="sm-row-texts">
        <span className="sm-row-label">{label}</span>
        {hint ? <span className="sm-row-hint">{hint}</span> : null}
      </span>
      <span className={`sm-switch ${checked ? "on" : ""}`}>
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="sm-switch-knob" />
      </span>
    </label>
  );
}

/** "HH:MM:SS" → "HH:MM" (input[type=time] saniye kabul etmez). */
const saatKirp = (v) => {
  const s = String(v || "").trim();
  if (!s) return "";
  const m = s.match(/^(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : "";
};

export default function SettingsModal({ open, onClose }) {
  const dispatch = useDispatch();
  const prefs = useSelector(selectPreferences);
  const loaded = useSelector(selectPreferencesLoaded);
  const saving = useSelector(selectPreferencesSaving);

  const gorevModulu = useModuleEnabled("internal_tasks");
  const mesajModulu = useModuleEnabled("mesajlasma");

  useEffect(() => {
    if (open) dispatch(fetchPreferences());
  }, [open, dispatch]);

  const muted = useMemo(
    () => (Array.isArray(prefs?.muted_categories) ? prefs.muted_categories : []),
    [prefs]
  );

  const gorunurSatirlar = useMemo(
    () =>
      SOURCE_ROWS.filter((r) => {
        if (r.module === "internal_tasks") return gorevModulu;
        if (r.module === "mesajlasma") return mesajModulu;
        return true;
      }),
    [gorevModulu, mesajModulu]
  );

  // FAZ-106 — EFEKTİF tür listesi sunucudan gelir. Ekran ham sözlüğü okuyamaz:
  // boş sözlük "kişi hiç dokunmadı" demektir ve o durumda doğru cevap ROLE
  // göre değişir (başkanda kapalı, müdürde açık). Rol tablosunu ön yüze
  // kopyalamak, aynı kuralın iki yerde yaşaması olurdu.
  const turSatirlari = useMemo(
    () => (Array.isArray(prefs?.bildirim_turleri) ? prefs.bildirim_turleri : []),
    [prefs]
  );

  // ── TEK LİSTE: gruplar + her grubun satırları ────────────────────────────
  //
  // Satırlar iki KAYNAKTAN gelir (tür ve kaynak anahtarı) ama ekranda ayırt
  // edilmezler: kullanıcı için ikisi de "bu bildirim bana gelsin mi". Ayrımı
  // yalnız `tip` alanı taşır ve o da sadece hangi alana yazılacağını söyler.
  const gruplar = useMemo(() => {
    const turlerKaynagaGore = new Map();
    turSatirlari.forEach((t) => {
      const k = String(t?.kaynak || "");
      if (!turlerKaynagaGore.has(k)) turlerKaynagaGore.set(k, []);
      turlerKaynagaGore.get(k).push(t);
    });

    return GRUPLAR.map((g) => {
      const satirlar = [];
      g.kaynaklar.forEach((kaynak) => {
        (turlerKaynagaGore.get(kaynak) || []).forEach((t) =>
          satirlar.push({
            tip: "tur",
            id: t.kod,
            label: t.etiket,
            hint: t.ipucu,
            acik: Boolean(t.acik),
          })
        );
        const kaynakSatiri = gorunurSatirlar.find((r) => r.key === kaynak);
        if (kaynakSatiri) {
          satirlar.push({
            tip: "kaynak",
            id: kaynak,
            label: SOURCE_LABEL_OVERRIDE[kaynak] || SOURCE_LABELS[kaynak] || kaynak,
            hint: HINTS[kaynak],
            acik: !muted.includes(kaynak),
          });
        }
      });
      return { ...g, satirlar };
    }).filter((g) => g.satirlar.length > 0);
  }, [turSatirlari, gorunurSatirlar, muted]);

  const save = (patch) => dispatch(savePreferences({ ...prefs, ...patch }));

  // Tek anahtar çevrilirken diğerleri KORUNUR: sunucu `tur_tercihleri`yi
  // bütün olarak değiştirir (PATCH-in-place değil), eksik gönderilen anahtar
  // varsayılana geri düşerdi.
  const setTurAcik = (kod, acik) => {
    const mevcut =
      prefs?.tur_tercihleri && typeof prefs.tur_tercihleri === "object"
        ? prefs.tur_tercihleri
        : {};
    save({ tur_tercihleri: { ...mevcut, [kod]: Boolean(acik) } });
  };

  const setCategoryEnabled = (key, enabled) => {
    const set = new Set(muted);
    if (enabled) set.delete(key);
    else set.add(key);
    save({ muted_categories: Array.from(set) });
  };

  // Sessiz saatler tek anahtarla açılıp kapanır: kapatmak iki alanı da null
  // yapar (backend "ikisi de dolu değilse kural yok" mantığıyla çalışır).
  const sessizAcik = Boolean(prefs?.quiet_hours_start || prefs?.quiet_hours_end);
  const setSessiz = (acik) =>
    acik
      ? save({ quiet_hours_start: "22:00", quiet_hours_end: "08:00" })
      : save({ quiet_hours_start: null, quiet_hours_end: null });

  const setSatir = (satir, acik) =>
    satir.tip === "tur" ? setTurAcik(satir.id, acik) : setCategoryEnabled(satir.id, acik);

  // "Tümünü aç" ARTIK İKİSİNİ BİRDEN açar. Eskiden yalnız kaynak susturmalarını
  // temizliyordu; tür anahtarları kapalı kaldığı için düğme "tümünü açtım" deyip
  // bazı satırları kapalı bırakıyordu — sözünü tutmayan bir kısayol.
  const kapaliSatirlar = gruplar.flatMap((g) => g.satirlar).filter((s) => !s.acik);
  const hepsiAcik = kapaliSatirlar.length === 0;
  const tumunuAc = () => {
    const yeniTurler = { ...(prefs?.tur_tercihleri || {}) };
    kapaliSatirlar
      .filter((s) => s.tip === "tur")
      .forEach((s) => {
        yeniTurler[s.id] = true;
      });
    save({ muted_categories: [], tur_tercihleri: yeniTurler });
  };

  return (
    <BasicModal
      open={open}
      onClose={onClose}
      title="Bildirim Ayarları"
      size="sm"
      actions={
        <button type="button" className="btn-md btn-md-primary" onClick={onClose}>
          Kapat
        </button>
      }
    >
      <div className="sm-body">
        {saving ? <div className="sm-saving">Kaydediliyor…</div> : null}

        {/* ══════════════════════════════════════════════════════════════════
            FAZ-84 — PENCERE "NEREYE GELSİN" MANTIĞIYLA YENİDEN DÜZENLENDİ.

            Kullanıcı (2026-08-01): "bildirim ayarlarının kısmını düzelt, şu anki
            hali güzel değil, mantıklı bir tasarım haline getirelim."

            Eski sıra: Genel → (telefon kutusu) → Sessiz saatler → Kaynaklar.
            İki kusuru vardı:
              • "Genel" hiçbir şey anlatmıyordu (ses+toast aslında BU EKRANA
                ait ayarlar), telefon bildirimi ise araya sıkışmış yabancı bir
                kutuydu.
              • "Sessiz saatler" ayrı bir üst bölümdü; bu, ayarın HER ŞEYİ
                susturduğu izlenimini veriyordu. Oysa yalnız TELEFONU susturur
                (zil ve ekran uyarıları etkilenmez).

            Yeni sıra tek bir soruyu izler: BİLDİRİM NEREYE GELSİN?
              1) Bu ekranda  → ses + toast
              2) Telefonuma  → cihaz kurulumu + ana anahtar + sessiz saat
              3) Neler bildirilsin → kaynaklar (ikisini de kapsar)
            Sessiz saat artık ait olduğu yerde: "Telefonuma" başlığının altında.
            ══════════════════════════════════════════════════════════════════ */}

        <div className="sm-section-title">Bu ekranda</div>
        <p className="sm-desc">Panel açıkken bilgisayarınızda/telefonunuzda ne olsun.</p>
        <Toggle
          label="Bildirim sesi"
          hint="Yeni bildirimde kısa bir uyarı sesi çalar."
          checked={Boolean(prefs?.sound_enabled)}
          onChange={(v) => save({ sound_enabled: v })}
          disabled={!loaded}
        />
        <Toggle
          label="Ekran uyarıları"
          hint="Sağ üstte açılır bildirim kartları gösterilir."
          checked={Boolean(prefs?.toast_enabled)}
          onChange={(v) => save({ toast_enabled: v })}
          disabled={!loaded}
        />

        {/* ── Telefonuma ─────────────────────────────────────────────────── */}
        <div className="sm-section-title">Telefonuma</div>
        <p className="sm-desc">
          Panel <b>kapalıyken bile</b> telefonunuza bildirim gelir: size talep
          atandığında, onayınız beklendiğinde ve mesaj geldiğinde.
        </p>
        <PushKurulumPaneli />
        {/* FAZ-100 — ANAHTAR VARSAYILAN KAPALI (kullanıcı: "izin kısmı otomatik
            geliyor, izin işini kendi versin"). Eskiden `!== false` yazıyordu;
            yani sunucu ne derse desin, hatta alan hiç gelmese bile AÇIK
            görünüyordu — kimse dokunmadan rıza varsayılmış oluyordu. Artık
            yalnız gerçekten `true` ise açık: anahtar, kişinin cihazında izni
            verdiği anda (sunucu tarafında) açılır. */}
        <Toggle
          label="Telefon bildirimi gönderilsin"
          hint="Cihazınızda bildirime izin verdiğinizde kendiliğinden açılır. Kapatırsanız cihazlarınız kayıtlı kalır, yalnız bildirim gönderilmez."
          checked={prefs?.push_enabled === true}
          onChange={(v) => save({ push_enabled: v })}
          disabled={!loaded}
        />
        <Toggle
          label="Gece sessiz olsun"
          // FAZ-84: quiet_hours_* alanları FAZ-31'den beri duruyordu ama
          // HİÇBİR ŞEY yapmıyordu (ölü alan). Artık gerçek işi var: yalnız
          // TELEFON bildirimini susturur. Metin de buna göre düzeltildi —
          // eskisi yapmadığı bir şeyi vaat ediyordu.
          hint="Bu aralıkta telefonunuz çalmaz; bildirimler zilde birikir. Süresi geçen talepler yine bildirilir."
          checked={sessizAcik}
          onChange={setSessiz}
          disabled={!loaded}
        />
        {sessizAcik ? (
          <div className="sm-times">
            <label className="sm-time">
              <span className="sm-time-label">Başlangıç</span>
              <input
                type="time"
                className="md-input"
                value={saatKirp(prefs?.quiet_hours_start)}
                disabled={!loaded}
                onChange={(e) => save({ quiet_hours_start: e.target.value || null })}
              />
            </label>
            <label className="sm-time">
              <span className="sm-time-label">Bitiş</span>
              <input
                type="time"
                className="md-input"
                value={saatKirp(prefs?.quiet_hours_end)}
                disabled={!loaded}
                onChange={(e) => save({ quiet_hours_end: e.target.value || null })}
              />
            </label>
          </div>
        ) : null}

        {/* ── Neler bildirilsin — TEK LİSTE (FAZ-106/2) ──────────────────
            Gruplar bildirim merkezindeki SEKMELERLE aynı: kişi zilde gördüğü
            bölmeyi burada aynı adla bulur. Her bildirim listede BİR KEZ geçer;
            hangi alana yazıldığı (tür / kaynak) kullanıcıyı ilgilendirmez. */}
        <div className="sm-section-head">
          <span className="sm-section-title">Neler bildirilsin</span>
          {/* Tek tek açmak yerine toplu kısayol. Zaten hepsi açıksa çizilmez. */}
          {!hepsiAcik ? (
            <button
              type="button"
              className="sm-linkbtn"
              onClick={tumunuAc}
              disabled={!loaded}
            >
              Tümünü aç
            </button>
          ) : null}
        </div>
        <p className="sm-desc">
          Kapattığınız bildirimler size <b>ses, ekran uyarısı ve telefon
          bildirimi</b> olarak gelmez. Bazı bildirimler görevinize göre
          varsayılan olarak kapalı gelir; açtığınızda bundan sonrakiler
          bildirilir.
        </p>
        {gruplar.map((g) => (
          <div className="sm-group" key={g.key}>
            <div className="sm-group-head">{g.label}</div>
            {g.satirlar.map((s) => (
              <Toggle
                key={`${s.tip}:${s.id}`}
                label={s.label}
                hint={s.hint}
                checked={s.acik}
                onChange={(v) => setSatir(s, v)}
                disabled={!loaded}
              />
            ))}
          </div>
        ))}
      </div>
    </BasicModal>
  );
}
